import { useEffect, useState } from "react";
import { isInsideTeams } from "./useOnline";
import {
  awaitPlatformAuthHandoff,
  isPlatformLaunch,
  readPlatformSession,
} from "@/lib/platformAuth";

type BrowserAuthState = "checking" | "authenticated" | "redirecting";

function redirectToLogin(): void {
  window.location.href = `/.auth/login/aad?post_login_redirect_url=${encodeURIComponent(
    window.location.pathname + window.location.search,
  )}`;
}

/**
 * For browser (non-Teams) sessions, verifies the user is signed in via
 * Azure App Service Easy Auth. If not, redirects to the Microsoft login page.
 *
 * - Inside Teams: immediately returns "authenticated" — Teams SSO (useTeamsAuth)
 *   handles identity; no Easy Auth session check is needed.
 * - Platform Hub new-tab launch: waits for Auth-Kit token handoff via postMessage
 *   and skips Easy Auth when a WorkOS session is present.
 * - Outside Teams: calls /.auth/me to check for an active session.
 *   Unauthenticated → redirects to /.auth/login/aad (Microsoft login).
 *   /.auth/me returns 401 (Easy Auth active, no session) → redirects to login.
 *   /.auth/me returns 404 (Easy Auth not configured, e.g. local dev) → returns
 *   "authenticated" so the app works normally without auth configured.
 *
 * Fail-open only when Easy Auth is absent (404). Network / unexpected errors
 * redirect to login rather than silently opening the app.
 *
 * This approach keeps Azure Easy Auth set to "Allow unauthenticated requests",
 * which lets the Teams iframe load without being blocked by a 302 redirect.
 * The browser auth wall is enforced here in the client instead.
 */
export function useBrowserAuth(): BrowserAuthState {
  const [state, setState] = useState<BrowserAuthState>(() =>
    isInsideTeams() ? "authenticated" : "checking",
  );

  useEffect(() => {
    if (isInsideTeams()) return;

    let cancelled = false;

    async function check() {
      // Platform Hub → new tab: accept Auth-Kit handoff before Easy Auth redirect.
      if (readPlatformSession() || isPlatformLaunch()) {
        const platform = readPlatformSession()
          ? readPlatformSession()
          : await awaitPlatformAuthHandoff();
        if (cancelled) return;
        if (platform?.accessToken) {
          setState("authenticated");
          return;
        }
        // Hub launch without a token — fall through to Easy Auth (fail closed in prod).
      }

      try {
        const res = await fetch("/.auth/me");
        if (cancelled) return;

        if (res.status === 404) {
          // Easy Auth not configured (local dev or auth disabled) — let the app through.
          setState("authenticated");
          return;
        }

        if (res.status === 401) {
          setState("redirecting");
          redirectToLogin();
          return;
        }

        if (!res.ok) {
          // Easy Auth is present but the session check failed — do not fail open.
          setState("redirecting");
          redirectToLogin();
          return;
        }

        const data = (await res.json()) as unknown;
        if (cancelled) return;

        if (Array.isArray(data) && data.length > 0) {
          setState("authenticated");
        } else {
          setState("redirecting");
          redirectToLogin();
        }
      } catch {
        // Network error while Easy Auth may be required — redirect rather than
        // silently treating the user as authenticated.
        if (!cancelled) {
          setState("redirecting");
          redirectToLogin();
        }
      }
    }

    void check();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
